import React from 'react';
import styled from 'styled-components';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn } from 'react-icons/fa';

const FooterSection = styled.footer`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.dark}, ${({ theme }) => theme.colors.darkGrey});
  color: ${({ theme }) => theme.colors.white};
  padding: 80px ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 60px ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const FooterColumn = styled.div``;

const LogoImage = styled.img`
  height: 150px;
  width: auto;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: white;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const FooterDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: ${({ theme }) => theme.fontSizes.base};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  max-width: 350px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  transition: all ${({ theme }) => theme.transitions.base};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-4px);
  }
`;

const ColumnTitle = styled.h4`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const LinkItem = styled.li`
  a {
    color: rgba(255, 255, 255, 0.8);
    font-size: ${({ theme }) => theme.fontSizes.base};
    transition: color ${({ theme }) => theme.transitions.fast};
    display: inline-block;
    
    &:hover {
      color: ${({ theme }) => theme.colors.white};
      transform: translateX(4px);
    }
  }
`;

const FooterBottom = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin: 0;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

const LegalLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

export const Footer = () => {
  const handleLinkClick = (e, id) => {
    e.preventDefault();
    scrollToSection(id);
  };

  return (
    <FooterSection>
      <Container>
        <FooterContent>
          <FooterColumn>
            <LogoImage src="/assets/logo.jpeg" alt="Logg Healthcare" />
            <FooterDescription>
              Revolutionizing Revenue Cycle Management for modern healthcare. 
              Streamline your operations with our advanced technology, proven processes, and expert talent.
            </FooterDescription>
            <SocialLinks>
              <SocialLink 
                href="https://wa.me/919840832469" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Contact us on WhatsApp"
              >
                <FaTwitter />
              </SocialLink>
              <SocialLink 
                href="https://www.instagram.com/sivakumar" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit our Instagram page"
              >
                <FaInstagram />
              </SocialLink>
              <SocialLink 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit our LinkedIn page"
              >
                <FaLinkedinIn />
              </SocialLink>
              <SocialLink 
                href="mailto:sid@logghealthcare.com" 
                aria-label="Email us"
              >
                <FaFacebookF />
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>Quick Links</ColumnTitle>
            <LinkList>
              <LinkItem>
                <a href="#home" onClick={(e) => handleLinkClick(e, 'home')}>Home</a>
              </LinkItem>
              <LinkItem>
                <a href="#services" onClick={(e) => handleLinkClick(e, 'services')}>Services</a>
              </LinkItem>
              <LinkItem>
                <a href="#about" onClick={(e) => handleLinkClick(e, 'about')}>About Us</a>
              </LinkItem>
              <LinkItem>
                <a href="#features" onClick={(e) => handleLinkClick(e, 'features')}>Why Choose Us</a>
              </LinkItem>
              <LinkItem>
                <a href="#appointment" onClick={(e) => handleLinkClick(e, 'appointment')}>Book Appointment</a>
              </LinkItem>
            </LinkList>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>Services</ColumnTitle>
            <LinkList>
              <LinkItem>
                <a href="#services" onClick={(e) => handleLinkClick(e, 'services')}>Medical Billing</a>
              </LinkItem>
              <LinkItem>
                <a href="#services" onClick={(e) => handleLinkClick(e, 'services')}>Medical Coding</a>
              </LinkItem>
              <LinkItem>
                <a href="#services" onClick={(e) => handleLinkClick(e, 'services')}>AR Management</a>
              </LinkItem>
              <LinkItem>
                <a href="#services" onClick={(e) => handleLinkClick(e, 'services')}>Healthcare Automation</a>
              </LinkItem>
            </LinkList>
          </FooterColumn>
          
          <FooterColumn>
            <ColumnTitle>Contact</ColumnTitle>
            <LinkList>
              <LinkItem>
                <a href="tel:+18187324513">+1 (818) 732-4513</a>
              </LinkItem>
              <LinkItem>
                <a href="mailto:sid@logghealthcare.com">sid@logghealthcare.com</a>
              </LinkItem>
              <LinkItem>
                <a href="#contact" onClick={(e) => handleLinkClick(e, 'contact')}>Get In Touch</a>
              </LinkItem>
              <LinkItem>
                <a href="#appointment" onClick={(e) => handleLinkClick(e, 'appointment')}>Request Consultation</a>
              </LinkItem>
            </LinkList>
          </FooterColumn>
        </FooterContent>
        
        <FooterBottom>
          <Copyright>
            © {new Date().getFullYear()} Logg Healthcare. All rights reserved.
          </Copyright>
          <LegalLinks>
            <LegalLink href="#privacy">Privacy Policy</LegalLink>
            <LegalLink href="#terms">Terms of Service</LegalLink>
            <LegalLink href="#hipaa">HIPAA Compliance</LegalLink>
          </LegalLinks>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};
